import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { getAuthToken } from '@/utils/auth';
import { API_CONFIG } from '@/constants/api';

export enum DoctorDocumentType {
  CRM = 'CRM',
  SPECIALTY_CERTIFICATE = 'SPECIALTY_CERTIFICATE',
  INSCRIPTION_PROOF = 'INSCRIPTION_PROOF',
  CONFIRMATION_VIDEO = 'CONFIRMATION_VIDEO',
  DIGITAL_CERTIFICATE = 'DIGITAL_CERTIFICATE',
}

export type DigitalCertificateKind = 'A1' | 'A2' | 'A3' | 'A4';

export interface DoctorDocument {
  id: string;
  doctorId: string;
  documentType: DoctorDocumentType;
  fileUrl?: string;
  originalFileName: string;
  mimeType: string;
  status: string;
  fileSize?: number;
  rejectionReason?: string;
  createdAt: string;
  // Campos legados para compatibilidade com outros componentes
  type?: string;
  fileName?: string;
  uploadedAt?: string;
  certificateKind?: DigitalCertificateKind | null;
  hasCertificatePassword?: boolean;
  requiresPasswordToUnlock?: boolean;
}

// Auxiliares de normalização de documentos para suportar múltiplos formatos de API (antiga/produção e nova/local)
export const normalizeDocument = (rawDoc: any): DoctorDocument | null => {
  if (!rawDoc || typeof rawDoc !== 'object') return null;

  const id = rawDoc.id || rawDoc.documentId;
  const fileUrl = rawDoc.fileUrl || rawDoc.url || rawDoc.path || '';
  if (!id) return null;

  // Garantir mapeamento correto dos enums
  let documentType = DoctorDocumentType.CRM;
  const rawType = rawDoc.documentType || rawDoc.type;
  if (
    rawType === 'SPECIALTY_CERTIFICATE' ||
    rawType === 'INSCRIPTION_PROOF' ||
    rawType === 'CRM' ||
    rawType === 'CONFIRMATION_VIDEO' ||
    rawType === 'DIGITAL_CERTIFICATE'
  ) {
    documentType = rawType as DoctorDocumentType;
  }

  const originalFileName = rawDoc.originalFileName || rawDoc.fileName || rawDoc.name || 'Documento';
  const createdAt = rawDoc.createdAt || rawDoc.uploadedAt || new Date().toISOString();

  return {
    id,
    doctorId: rawDoc.doctorId || '',
    documentType,
    type: documentType, // alias legado
    fileUrl,
    originalFileName,
    fileName: originalFileName, // alias legado
    mimeType: rawDoc.mimeType || 'application/pdf',
    status: rawDoc.status || 'PENDING_REVIEW',
    fileSize: rawDoc.fileSize || 0,
    rejectionReason: rawDoc.rejectionReason,
    createdAt,
    uploadedAt: createdAt, // alias legado
    certificateKind: rawDoc.certificateKind ?? null,
    hasCertificatePassword: Boolean(rawDoc.hasCertificatePassword),
    requiresPasswordToUnlock: Boolean(
      rawDoc.requiresPasswordToUnlock || documentType === DoctorDocumentType.DIGITAL_CERTIFICATE,
    ),
  };
};

export const normalizeDocumentsList = (data: any): DoctorDocument[] => {
  if (!data) return [];

  let rawList: any[] = [];

  if (data.documents) {
    rawList = Array.isArray(data.documents) ? data.documents : [data.documents];
  } else if (data.data) {
    rawList = Array.isArray(data.data) ? data.data : [data.data];
  } else if (Array.isArray(data)) {
    rawList = data;
  } else if (typeof data === 'object' && (data.id || data.fileUrl)) {
    rawList = [data];
  }

  return rawList
    .map(normalizeDocument)
    .filter((doc): doc is DoctorDocument => doc !== null);
};

export const useMedicalDocuments = (doctorId: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<DoctorDocument[]>([]);

  const fetchDocuments = useCallback(async () => {
    setIsFetching(true);
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/doctors/${doctorId}/documents`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const contentType = response.headers.get('content-type');
      let data = null;
      if (contentType?.includes('application/json')) {
        data = await response.json();
      }

      if (response.ok && data) {
        // Normaliza a lista obtida do GET
        const normalized = normalizeDocumentsList(data);
        setDocuments(normalized);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setIsFetching(false);
    }
  }, [doctorId]);

  const uploadDocument = async (
    file: File,
    type: DoctorDocumentType,
    options?: {
      certificateKind?: DigitalCertificateKind;
      certificatePassword?: string;
    },
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      if (!token) throw new Error('Token de autenticação não encontrado.');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      formData.append('documentType', type);
      if (options?.certificateKind) {
        formData.append('certificateKind', options.certificateKind);
      }
      if (options?.certificatePassword) {
        formData.append('certificatePassword', options.certificatePassword);
      }

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/doctors/${doctorId}/documents`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const contentType = response.headers.get('content-type');
      let data = null;
      if (contentType?.includes('application/json')) {
        data = await response.json();
      }

      if (!response.ok || !data?.success) {
        throw new Error(data?.error || data?.message || `Erro ao fazer upload do documento (${response.status})`);
      }

      // Normalizar a resposta de upload usando a mesma lógica resiliente
      const normalizedList = normalizeDocumentsList(data);

      if (normalizedList.length === 0) {
        console.error('Falha ao normalizar resposta do upload. Dados crus:', data);
        throw new Error('Formato de resposta inválido: Nenhum documento retornado.');
      }

      const uploadedDoc = normalizedList[0];
      if (!uploadedDoc.id) {
        throw new Error('Metadados essenciais do documento (id) ausentes.');
      }

      toast.success(`${type} enviado com sucesso!`);
      await fetchDocuments();
      return true;
    } catch (err: any) {
      const msg = err.message || 'Erro desconhecido';
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDocument = async (documentId: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/doctors/${doctorId}/documents/${documentId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const contentType = response.headers.get('content-type');
      let data = null;
      if (contentType?.includes('application/json')) {
        data = await response.json();
      }

      if (response.ok && data?.success) {
        toast.success('Documento removido com sucesso');
        await fetchDocuments();
        return true;
      }
      throw new Error(data?.error || data?.message || `Erro ao deletar documento (${response.status})`);
    } catch (err: any) {
      toast.error(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const getDownloadUrl = (documentId: string) => {
    const token = getAuthToken();
    return `${API_CONFIG.BASE_URL}/doctors/${doctorId}/documents/${documentId}?token=${token}`;
  };

  return {
    uploadDocument,
    uploadDocuments: uploadDocument,
    deleteDocument,
    fetchDocuments,
    getDoctorDocuments: fetchDocuments,
    getDownloadUrl,
    documents,
    isLoading,
    loading: isLoading,
    isFetching,
    error
  };
};
